// VERIFY-01 — persisted reconciliation oracle runs (read-only).
//
// Runs are started by the CLI (`python -m app.cli.verify run --shop …`) or the daily beat, never from
// here: this page lists the operator's own shop's runs (`/verification/runs`) and drills into one run's
// immutable findings (`/verification/runs/{id}`), filtered by check and severity. Amounts are exact
// integer minor units with their currency + exponent (formatReconciledMinor, no float); a run that did
// not complete shows its counts as unknown, never 0. Backend: app/api/console_portal/verification.py.
import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, TriangleAlert } from "lucide-react";

import { ApiError, apiGet } from "../api";
import { formatReconciledMinor } from "../money";

type Severity = "error" | "warning" | "unknown";

interface CheckResult {
  title: string;
  scope: string;
  status: string;
  subjects_checked: number;
  findings: Record<Severity, number>;
  duration_ms: number;
  failure_code: string | null;
}

export interface VerificationRun {
  run_id: string;
  status: string;
  trigger: string;
  actor_ref: string;
  environment: string;
  requested_checks: string[];
  since_at: string | null;
  started_at: string;
  finished_at: string | null;
  counts: Record<Severity, number> | null;
  checks: Record<string, CheckResult> | null;
  findings_digest_sha256: string | null;
  failure_code: string | null;
}

export interface VerificationFinding {
  finding_id: string;
  ordinal: number;
  check_id: string;
  code: string;
  severity: Severity;
  subject: { kind: string; ref: string };
  related_refs: string[];
  money: { currency: string; exponent: number; expected_minor: string | null; observed_minor: string | null } | null;
  evidence_refs: string[];
  detail: Record<string, unknown>;
  record_digest_sha256: string;
  integrity: string;
}

interface RunList {
  runs: VerificationRun[];
  has_more: boolean;
  next_offset: number | null;
}

interface RunDetail {
  run: VerificationRun;
  total: number;
  findings: VerificationFinding[];
  has_more: boolean;
  next_offset: number | null;
}

const SEVERITIES: { value: "all" | Severity; label: string }[] = [
  { value: "all", label: "All" },
  { value: "error", label: "Errors" },
  { value: "warning", label: "Warnings" },
  { value: "unknown", label: "Unknown" },
];

function when(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function amount(minor: string | null, money: NonNullable<VerificationFinding["money"]>): string {
  return minor === null ? "unknown" : formatReconciledMinor(minor, money.exponent, money.currency);
}

export function runStatusClass(run: VerificationRun): string {
  if (run.status !== "completed") return "verify-status-failed";
  if (run.counts && run.counts.error > 0) return "verify-status-error";
  if (run.counts && run.counts.warning > 0) return "verify-status-warning";
  return "verify-status-ok";
}

export function Counts({ run }: { run: VerificationRun }) {
  if (!run.counts) {
    return <span className="dlq-sub">counts unknown ({run.status}{run.failure_code ? `: ${run.failure_code}` : ""})</span>;
  }
  return (
    <span className="dlq-sub">
      {run.counts.error} error{run.counts.error === 1 ? "" : "s"} · {run.counts.warning} warning
      {run.counts.warning === 1 ? "" : "s"} · {run.counts.unknown} unknown
    </span>
  );
}

function FindingRow({ finding }: { finding: VerificationFinding }) {
  return (
    <div className="verify-finding">
      <span className={`dlq-status verify-sev-${finding.severity}`}>{finding.severity}</span>
      <div className="dlq-main">
        <span className="dlq-event">
          <span className="verify-check">{finding.check_id}</span> {finding.code}
        </span>
        <span className="dlq-sub">
          {finding.subject.kind}: <span className="verify-ref">{finding.subject.ref}</span>
        </span>
        {finding.money && (
          <span className="dlq-sub verify-money">
            expected {amount(finding.money.expected_minor, finding.money)} · observed{" "}
            {amount(finding.money.observed_minor, finding.money)}
          </span>
        )}
        {finding.related_refs.length > 0 && (
          <span className="dlq-sub">related: {finding.related_refs.join(", ")}</span>
        )}
        {finding.evidence_refs.length > 0 && (
          <span className="dlq-sub">evidence: {finding.evidence_refs.join(", ")}</span>
        )}
        {Object.keys(finding.detail).length > 0 && (
          <details className="verify-detail">
            <summary>detail</summary>
            <pre>{JSON.stringify(finding.detail, null, 2)}</pre>
          </details>
        )}
      </div>
      <span className={finding.integrity === "verified" ? "dlq-sub" : "dlq-error"}>
        {finding.integrity === "verified" ? "digest verified" : "DIGEST MISMATCH"}
      </span>
    </div>
  );
}

/** B11: the Kernvel-staff cross-shop page reuses this view with its own staff-token getter. */
export type RunGetter = <T>(path: string) => Promise<T>;

export function RunDetailView({ runId, get = apiGet as RunGetter }: { runId: string; get?: RunGetter }) {
  const [severity, setSeverity] = useState<"all" | Severity>("all");
  const [checkId, setCheckId] = useState<string>("all");
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(
    async (offset: number) => {
      const params = new URLSearchParams();
      if (severity !== "all") params.set("severity", severity);
      if (checkId !== "all") params.set("check_id", checkId);
      if (offset) params.set("offset", String(offset));
      const qs = params.toString();
      try {
        const page = await get<RunDetail>(`/verification/runs/${runId}${qs ? `?${qs}` : ""}`);
        setDetail((prev) => (offset && prev ? { ...page, findings: [...prev.findings, ...page.findings] } : page));
        setErr(null);
      } catch (e) {
        setErr(e instanceof Error && e.message ? e.message : "Failed to load the run");
      }
    },
    [runId, severity, checkId, get],
  );

  useEffect(() => {
    void load(0);
  }, [load]);

  const run = detail?.run;
  return (
    <section className="lane verify-detail-lane">
      <div className="lane-head">
        <span>Run {runId.slice(0, 8)}…</span>
        {detail && <span className="lane-count">{detail.total}</span>}
      </div>
      <div className="lane-body">
        {err && <div className="banner-err">{err}</div>}
        {run && (
          <>
            <div className="card-meta">
              <span>status {run.status}</span>
              <span>{run.trigger} · {run.actor_ref}</span>
              <span>started {when(run.started_at)}</span>
              <span>finished {when(run.finished_at)}</span>
              {run.since_at && <span>since {when(run.since_at)}</span>}
              {run.findings_digest_sha256 && (
                <span className="verify-ref">digest {run.findings_digest_sha256.slice(0, 16)}…</span>
              )}
            </div>
            {run.checks && (
              <div className="cash-table-wrap">
                <table className="cash-table">
                  <thead>
                    <tr>
                      <th>Check</th>
                      <th>Invariant</th>
                      <th>Status</th>
                      <th>Subjects</th>
                      <th>Errors</th>
                      <th>Warnings</th>
                      <th>Unknown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(run.checks).map(([id, result]) => (
                      <tr key={id}>
                        <td className="mono">{id}</td>
                        <td>{result.title}</td>
                        <td className={`verify-check-${result.status}`}>{result.status}</td>
                        <td className="mono">{result.subjects_checked}</td>
                        <td className="mono">{result.findings.error}</td>
                        <td className="mono">{result.findings.warning}</td>
                        <td className="mono">{result.findings.unknown}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="dlq-tabs">
              {SEVERITIES.map((item) => (
                <button
                  key={item.value}
                  className={`dlq-tab ${severity === item.value ? "active" : ""}`}
                  onClick={() => setSeverity(item.value)}
                >
                  {item.label}
                </button>
              ))}
              <select aria-label="Check" value={checkId} onChange={(e) => setCheckId(e.target.value)}>
                <option value="all">All checks</option>
                {Object.keys(run.checks ?? {}).map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </div>
            {detail.findings.length === 0 ? (
              <p className="lane-empty">
                {run.status === "completed" ? "No findings match." : "This run did not complete; findings unknown."}
              </p>
            ) : (
              detail.findings.map((finding) => <FindingRow key={finding.finding_id} finding={finding} />)
            )}
            {detail.has_more && detail.next_offset !== null && (
              <button className="dlq-tab" onClick={() => void load(detail.next_offset ?? 0)}>
                Load more findings
              </button>
            )}
          </>
        )}
        {!run && !err && <p className="lane-empty">Loading the run…</p>}
      </div>
    </section>
  );
}

export default function Verification() {
  const [list, setList] = useState<RunList | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const page = await apiGet<RunList>("/verification/runs?limit=50");
      setList(page);
      setErr(null);
      setSelected((current) => current ?? page.runs[0]?.run_id ?? null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load verification runs");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const latest = list?.runs[0];
  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Verification</h1>
      </div>
      <p className="cap-note">
        Persisted reconciliation oracles (M1–M11 money, O1–O4 operations, F1–F6 foundation vocabularies, S1–S3
        security, C1/CM1/CM4/CM5 catalog &amp; commerce, CA1 exceptions, SU1 support, FD1/VN1/GC1/GC2/SP1/SP2
        local commerce, DL2 delivery, B1/EC1 billing &amp; economics, OPS1–3/NAT1–3/NS1–3 operator &amp; native
        platform, AICAT1/AID1/AI1/AI2/UX1 AI &amp; UX, REL1 release-migration manifest). Read only: a run is
        started with{" "}
        <code>python -m app.cli.verify run --shop …</code>, a release-slice run with{" "}
        <code>python -m app.cli.verify slice --shop … --feature …</code>, or by the daily schedule, and its
        findings are immutable.
      </p>
      {err && (
        <div className="banner-err">
          {err}
          {list ? " — showing the last successful read, which may be stale." : ""}
        </div>
      )}
      <div className="kpis dlq-kpis">
        <div className={`kpi ${latest?.counts?.error ? "kpi-risk" : "kpi-done"}`}>
          <div className="kpi-icon">{latest?.counts?.error ? <TriangleAlert size={18} /> : <ShieldCheck size={18} />}</div>
          <div className="kpi-body">
            <div className="kpi-value">{latest?.counts ? latest.counts.error : "—"}</div>
            <div className="kpi-label">Errors in the latest run</div>
          </div>
        </div>
        <div className="kpi kpi-attention">
          <div className="kpi-icon">
            <TriangleAlert size={18} />
          </div>
          <div className="kpi-body">
            <div className="kpi-value">{latest?.counts ? latest.counts.unknown : "—"}</div>
            <div className="kpi-label">Unknown outcomes in the latest run</div>
          </div>
        </div>
      </div>
      <div className="verify-grid">
        <section className="lane">
          <div className="lane-head">
            <span>Runs</span>
            {list && <span className="lane-count">{list.runs.length}</span>}
          </div>
          <div className="lane-body">
            {!list ? (
              <p className="lane-empty">{err ? "Runs unavailable" : "Loading verification runs…"}</p>
            ) : list.runs.length === 0 ? (
              <p className="lane-empty">No verification run yet.</p>
            ) : (
              list.runs.map((run) => (
                <button
                  key={run.run_id}
                  className={`verify-run ${selected === run.run_id ? "active" : ""}`}
                  onClick={() => setSelected(run.run_id)}
                >
                  <span className={`dlq-status ${runStatusClass(run)}`}>{run.status}</span>
                  <span className="dlq-main">
                    <span className="dlq-event">{when(run.started_at)}</span>
                    <Counts run={run} />
                    <span className="dlq-sub">
                      {run.trigger} · {run.requested_checks.length} check{run.requested_checks.length === 1 ? "" : "s"}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </section>
        {selected ? <RunDetailView key={selected} runId={selected} /> : null}
      </div>
    </div>
  );
}
